import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Transition as _Transition, withCtx as _withCtx, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b16e8710"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 1,
  id: "campaign-complete",
  class: /*#__PURE__*/_normalizeClass({portrait: true})
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "dark-overlay" }, null, -1))
const _hoisted_3 = { id: "campaign-complete-info-summary-countdown-action" }
const _hoisted_4 = {
  id: "expandable-bottom-modal",
  style: {"overflow":"visible"}
}
const _hoisted_5 = { id: "campaign-complete-media-container" }
const _hoisted_6 = { id: "campaign-complete-info-container" }
const _hoisted_7 = { id: "campaign-complete-info" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", {
  id: "campaign-complete-info-header-title",
  style: {"font-size":"14px","font-weight":"400 !important"}
}, "Description", -1))
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("a", {
  class: "term-of-use-link",
  href: "https://www.souldrop.page/terms-of-use",
  target: "_blank",
  rel: "noreferrer"
}, "Terms of Use", -1))

import { TransactionResponse } from '@ethersproject/abstract-provider';
import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from '@heroicons/vue/solid';
import { ethers } from 'ethers';
import { parseEther } from 'ethers/lib/utils';
import JSConfetti from 'js-confetti';
import { computed, onMounted, ref, watch } from 'vue';
import { useMutation } from 'vue-query';
import { useStore } from 'vuex';
import { erc721Abi, erc1155Abi } from '@/abis';
import apiHelpers from '@/api/apiHelpers';
import collectorsApi, { IMerkleInfo } from '@/api/collectors/collectors';
import { ClaimLimitType } from '@/api/models/claim';
import ClaimExtensionContract, { Claim721, Claim721Payable, Claim1155, Claim1155Payable } from '@/classes/claimExtensionContract';
import { DEFAULT_DOMINANT_COLOR, DEFAULT_END_GRADIENT_COLOR, FREE_EXTENSION_MAINNET_721, FREE_EXTENSION_MAINNET_1155 } from '@/common/constants';
import Action from '@/components/Action.vue';
import ClaimDescription from '@/components/ClaimDescription.vue';
import ClaimSummary from '@/components/ClaimSummary.vue';
import ConfirmMintDialog from '@/components/ConfirmMintDialog.vue';
import Countdown from '@/components/Countdown.vue';
import Footer from '@/components/Footer.vue';
import Frame from '@/components/Frame.vue';
import Holders, { MembershipRecord } from '@/components/Holders.vue';
import ImageLoader from '@/components/ImageLoader.vue';
import PlatformLinks from '@/components/PlatformLinks.vue';
import ViewDetails, { Record } from '@/components/ViewDetails.vue';
import WalletConnection from '@/components/WalletConnection.vue';
import { useCollector } from '@/composables/useCollector';
import { getFirebaseClaim } from '@/firebase';
import { abbreviate, abbreviateAddress } from '@/lib/claimUtil';
import isDev from '@/lib/isDev';
import { AuthState } from '@/store/modules/account.store';


export default /*#__PURE__*/_defineComponent({
  __name: 'ClaimCompleteBody',
  props: {
  claimConfig: {
    type: Object,
    required: true
  }
},
  setup(__props) {

const props = __props;


const store = useStore();
const { authState, walletAddress, network, isProviderAvailable } = useCollector();
const maxTokenPerWallet = ref(0);
const tokenMinted = ref<number>();
const numberToMint = ref('1');
const tipAmount = ref(0);
const networkError = ref('');
const claim = ref<Claim721 | Claim1155 | Claim1155Payable | Claim721Payable>();
const txLink = ref('');
const tokenId = ref(0);
const DEV_FEE = parseEther('0.00069');
const membershipRecords = ref<Array<MembershipRecord>>([]);
const mediaArweaveUrl = ref('');
const isLoggedIn = computed(() => {
  return authState.value === AuthState.LOGGED_IN;
});
const networkToCheck = computed(() => {
  return isDev() ? 5 : 1;
});
const txConfirming = ref(false);

const claimLimitType = computed(() : ClaimLimitType => {
  if (!claim.value) {
    return 'unknown';
  }
  if (claim.value.merkleRoot !== ethers.utils.formatBytes32String('')) {
    return 'whitelist';
  } else if (claim.value.walletMax) {
    return 'limited';
  } else {
    return 'open';
  }
});

const allowMint = computed(() => {
  return allowedMintQuantity.value > 0 && claimTokenStatus.value !== 'loading';
});

const status = computed(() => {
  const c = props.claimConfig;
  const now = Date.now();

  if (!c) {
    return null;
  }

  if (c.startDate && c.startDate.getTime() > now) {
    return 'not-started';
  } else if (c.endDate && c.endDate.getTime() < now) {
    return 'ended';
  } else {
    return 'enabled';
  }
});

const isPayable = computed(() => {
  const claimRequirements = props.claimConfig;
  if (claimRequirements.claimType.toLowerCase() === 'erc1155') {
    return claimRequirements.extensionAddress.toLowerCase() !== FREE_EXTENSION_MAINNET_1155;
  }
  if (claimRequirements.claimType.toLowerCase() === 'erc721') {
    return claimRequirements.extensionAddress.toLowerCase() !== FREE_EXTENSION_MAINNET_721;
  }
  throw new Error(`Invalid contract type ${claimRequirements.claimType}`);
});

const arweaveUrl = computed(() => {
  if (claim.value && claim.value.location) {
    return `https://arweave.net/${claim.value.location}`;
  }
  return undefined;
});

const error = computed(() => {
  const err: any = claimTokenError.value || networkError.value;
  if (err) {
    let msg;
    if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
      msg = 'Transaction rejected';
    } else if (err.code === 'INSUFFICIENT_FUNDS') {
      msg = 'Not enough funds in wallet';
    } else if (err.code === 'TRANSACTION_REPLACED' && !err.cancelled && err.replacement) { // Check if the trasaction was sped up
      console.warn('Transaction replaced');
      msg = `Transaction was replaced, please check https://etherscan.io/tx/${err.replacement.hash} for the status of your transaction`;
    } else if (err.code === 'TRANSACTION_REPLACED' && err.cancelled) {
      msg = 'Transaction was cancelled';
    } else if (err.code === 'CALL_EXCEPTION') { // transaction was cancelled
      msg = 'Transaction Failed';
    } else if (err.message && err.message.includes('Ledger device')) {
      msg = 'Error with your ledger device, please try again';
    } else if (err.message && err.message.includes('Maximum tokens already minted')) {
      msg = 'Maximum tokens already minted for this wallet per allocation';
    } else if (err.error && err.error.message) {
      msg = err.error.message;
    } else if (err.error && typeof err.error === 'string' && err.error.includes('Address is not part of audience')) {
      msg = 'You are not in the allow list for this claim';
    } else if (err.error) {
      msg = err.error;
    } else if (err.message) {
      msg = err.message;
    } else if (typeof err === 'string') {
      msg = err;
    } else {
      msg = 'Transaction unable to complete';
    }
    return {
      error: msg
    };
  }
  return null;
});

const isSoldOut = computed(() => {
  return claim.value && claim.value.total > 0 && claim.value.total === claim.value.totalMax;
});

const backgroundStyle = computed(() => {
  return {
    backgroundImage: `url(${props.claimConfig.image})`
  };
});

const claimExtensionContract = ref<ClaimExtensionContract>(new ClaimExtensionContract(
  props.claimConfig?.extensionAddress,
  props.claimConfig?.creatorContractAddress,
  props.claimConfig?.claimIndex
));

const refreshClaim = async () : Promise<void> => {
  if (!claim.value) {
    try {
      claim.value = await claimExtensionContract.value.getClaim(props.claimConfig?.claimType);
    } catch (e) {
      console.warn(e);
      claim.value = undefined;
    }
  }
};

const getMerkleInfos = async () : Promise<IMerkleInfo[]> => {
  if (!walletAddress.value) {
    throw new Error('Unable to get your wallet address, please log out and try connecting your wallet again.');
  }
  return collectorsApi.getMerkleInfos(props.claimConfig?.slug, walletAddress.value);
};

const getMaxPerWallet = async () : Promise<void> => {
  if (!claim.value) {
    throw new Error('Unable to get claim from extension contract');
  }

  if (claimLimitType.value === 'whitelist') {
    const claimMerkleInfos = await getMerkleInfos();
    maxTokenPerWallet.value = claimMerkleInfos.length;
  } else {
    maxTokenPerWallet.value = claim.value.walletMax;
  }
};

const getTokenMinted = async (forcedRefresh = false) => {
  const claimRequirements = props.claimConfig;
  let mintedIndices : boolean[] = [];
  if (!claim.value) {
    throw new Error('Unable to get claim from extension contract');
  }
  if (!claimExtensionContract.value) {
    throw new Error('Unable to get minted amount');
  }
  if (typeof tokenMinted.value === 'undefined' || forcedRefresh) {
    if (claimLimitType.value === 'whitelist') {
      const claimMerkleInfos = await getMerkleInfos();
      const mintIndices = claimMerkleInfos.map(claimMerkleInfo => claimMerkleInfo.value);
      mintedIndices = await claimExtensionContract.value.checkMintIndices(mintIndices, claimRequirements.claimType);
      tokenMinted.value = mintedIndices.filter((mintedIndice) => mintedIndice).length;
    } else if (claimLimitType.value === 'limited') {
      if (!walletAddress.value) {
        throw new Error('Unable to get your wallet address, please log out and try connecting your wallet again.');
      }
      tokenMinted.value = await claimExtensionContract.value.getTotalMints(walletAddress.value, claimRequirements.claimType);
    }
  }
};

const refreshTokenMintedAndMaxPerWallet = async (forcedRefresh = false) => {
  return Promise.all([
    getMaxPerWallet(),
    getTokenMinted(forcedRefresh)
  ]);
};

const allowedMintQuantity = computed(() => {
  if (claimLimitType.value === 'open') {
    return 10; // Unlimited but we set to 10 for the dropdown
  }
  return maxTokenPerWallet.value - (tokenMinted.value || 0);
});

const getCustomEmojis = () => {
  return props.claimConfig?.data?.emojis ?? ['🙏'];
};

const {
  status: claimTokenStatus,
  error: claimTokenError,
  mutate: claimToken,
  isLoading
} = useMutation(async () => {
  const claimRequirements = props.claimConfig;
  try {
    await checkValidNetwork();
  } catch (e) {
    networkError.value = `Please switch to ${networkToCheck.value === 1 ? 'Mainnet' : 'Goerli'} network to continue.`;
    return;
  }
  if (!walletAddress.value) {
    throw new Error('User wallet not connected, please refresh the page and hit "Connect Wallet"');
  }
  // check if user allow to mint more
  await refreshTokenMintedAndMaxPerWallet(true);
  if (
    claimLimitType.value !== 'open' &&
        maxTokenPerWallet.value &&
        maxTokenPerWallet.value <= (tokenMinted.value || 0)
  ) {
    throw new Error('No more tokens to claim');
  }
  // get merkle proof
  let claimableMintIndices: number[] = [];
  let claimableMerkleProofs: string[][] = [];

  if (!claimExtensionContract.value) {
    throw new Error('Unable to claim tokens with missing contract instance');
  }
  const claim = await claimExtensionContract.value.getClaim(claimRequirements.claimType);

  if (claim.merkleRoot !== ethers.utils.formatBytes32String('')) {
    if (!walletAddress.value) {
      throw new Error('Unable to get your wallet address, please log out and try connecting your wallet again.');
    }
    const claimMerkleInfos = await getMerkleInfos();
    const mintIndices = claimMerkleInfos.map(claimMerkleInfo => claimMerkleInfo.value);
    const mintIndicesStatus = await claimExtensionContract.value.checkMintIndices(mintIndices, claimRequirements.claimType);
    const claimableMerkleInfo = claimMerkleInfos.filter((_, index) => !mintIndicesStatus[index]);
    claimableMintIndices = claimableMerkleInfo.map(claimMerkleInfo => claimMerkleInfo.value);
    claimableMerkleProofs = claimableMerkleInfo.map((claimMerkleInfo) => claimMerkleInfo.merkleProof);

    if (claimableMintIndices.length < parseInt(numberToMint.value)) {
      throw new Error('Invalid amount submitted');
    }
  }

  if (!walletAddress.value) throw new Error('Cannot mint to undefined wallet address');

  let response : TransactionResponse | undefined;

  if (tipAmount.value < 0) {
    throw new Error("Tip amount can't be negative.");
  }

  if (tipAmount.value > 999999999999999) {
    throw new Error("That's too much ETH. 15 leading digits max.");
  }

  if (tipAmount.value !== 0 && tipAmount.value < 0.000001) {
    throw new Error("That's too small. 6 decimal places max. ");
  }
  const tipAmountInEther = parseEther(tipAmount.value.toString() || '0');
  if (numberToMint.value === '1') {
    if (isPayable.value) {
      const amountBN = ethers.BigNumber.from(claim.cost).add(tipAmountInEther).add(DEV_FEE);
      response = await claimExtensionContract.value.mint(claimableMintIndices[0] || 0, claimableMerkleProofs[0] || [], claimRequirements.claimType, amountBN, walletAddress.value);
    } else {
      response = await claimExtensionContract.value.mint(claimableMintIndices[0] || 0, claimableMerkleProofs[0] || [], claimRequirements.claimType, undefined, walletAddress.value);
    }
  } else {
    const mintCount = parseInt(numberToMint.value);

    if (isPayable.value) {
      const amountBN = ethers.BigNumber.from(claim.cost).mul(mintCount).add(tipAmountInEther).add(DEV_FEE.mul(mintCount));
      response = await claimExtensionContract.value.mintBatch(mintCount, claimableMintIndices.slice(0, mintCount), claimableMerkleProofs.slice(0, mintCount), claimRequirements.claimType, amountBN, walletAddress.value);
    } else {
      response = await claimExtensionContract.value.mintBatch(mintCount, claimableMintIndices.slice(0, mintCount), claimableMerkleProofs.slice(0, mintCount), claimRequirements.claimType, undefined, walletAddress.value);
    }
  }
  txConfirming.value = true;
  if (response) {
    // record tx and IP address
    try {
      await fetch(`https://us-central1-claim-tip.cloudfunctions.net/recordTx?transaction=${response?.hash}&appId=2&network=${networkToCheck.value}`);
    } catch (e) {
      console.warn(e);
    }

    try {
      txLink.value = response.hash;
      const receipt = await response.wait(1);
      if (receipt && receipt.logs) {
        const ids = [];
        for (const log of receipt.logs) {
          try {
            if (claimRequirements.claimType.toLowerCase() === 'erc721') {
              const iface = new ethers.utils.Interface(erc721Abi);
              const parsed = iface.parseLog(log);
              ids.push(parsed.args.tokenId.toString());
            } else {
              const iface = new ethers.utils.Interface(erc1155Abi);
              const parsed = iface.parseLog(log);
              ids.push(parsed.args.id.toString());
            }
          } catch (e) {
            // Do nothing, log will failed parse for events specific to extension contract
          }
        }
        tokenId.value = ids[ids.length - 1];
      }
    } catch (e) {
      // do not throw for now
      console.log(e);
    }
    txConfirming.value = false;

    new JSConfetti().addConfetti({
      emojis: getCustomEmojis(),
      confettiRadius: 90,
      confettiNumber: 200
    });
  }

  await refreshTokenMintedAndMaxPerWallet(true);

  // manually trigger data refresh after 2s
  setTimeout(async () => {
    if (networkToCheck.value === 5) {
      await fetch('https://us-central1-claim-tip.cloudfunctions.net/addGoerliClaimSoulboundRecord');
      refershTipRecords();
    } else if (networkToCheck.value === 1) {
      await fetch('https://us-central1-claim-tip.cloudfunctions.net/addMainnetClaimSoulboundRecord');
      refershTipRecords();
    }
  }, 2000);
},
{
  onError: (_) => {
    txConfirming.value = false;
  }
});

const checkValidNetwork = async () : Promise<void> => {
  if (network.value === 'unknown') {
    throw new Error('Wrong network');
  }
};

const getMediaArweaveUrl = async () : Promise<string> => {
  if (arweaveUrl.value) {
    const arweaveUrlParse = await parseTokenMetadata(arweaveUrl.value);
    return arweaveUrlParse;
  }
  return '';
};

const parseTokenMetadata = async (url: string) : Promise<string> => {
  const response = await fetch(url);

  const metadata = await response.json();
  return metadata.animation_url || metadata.animation || metadata.image_url || metadata.image;
};

const refreshWeb3States = async () => {
  try {
    await refreshClaim();
    await refreshTokenMintedAndMaxPerWallet();
  } catch (e) {
    console.warn(e);
  }

  try {
    await checkValidNetwork();
  } catch (e) {
    networkError.value = `Please switch to ${networkToCheck.value === 1 ? 'Mainnet' : 'Goerli'} network to continue.`;
    return;
  }
  networkError.value = '';
};

watch(arweaveUrl, async _ => {
  // get on-chain data on load
  try {
    mediaArweaveUrl.value = await getMediaArweaveUrl();
  } catch (e) {
    console.warn(e);
  }
}, { immediate: true });

const refershTipRecords = async () => {
  try {
    const results = await getFirebaseClaim(networkToCheck.value, props.claimConfig?.extensionAddress.toLowerCase(), props.claimConfig.creatorContractAddress.toLowerCase(), props.claimConfig.claimIndex);
    const addresses = new Set(results.map((result) => result.fromAddress.toLowerCase()));
    const membershipRecordsTemp = [];
    for (const address of addresses) {
      // sort by date ASC
      const joinedDate = results.find((result) => result.fromAddress.toLowerCase() === address);
      membershipRecordsTemp.push({ walletAddress: address.toLowerCase(), date: joinedDate.created_at.toDate() });
    }
    membershipRecordsTemp.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    membershipRecords.value = membershipRecordsTemp;
    // try getting ens name
    const lookupAddressPromises = [];
    for (const record of membershipRecords.value) {
      lookupAddressPromises.push(claimExtensionContract.value.getEnsName(record.walletAddress));
    }
    const ensResults = await Promise.allSettled(lookupAddressPromises);
    for (let i = 0; i < ensResults.length; i++) {
      if (ensResults[i]?.status === 'fulfilled') {
        // @ts-ignore
        membershipRecords.value[i].ens = ensResults[i].value;
      }
    }
  } catch (e) {
    console.warn(e);
  }
};

// refetch user data when error to update state correctly
watch(error, rsp => {
  if (rsp && rsp.length) {
    store.dispatch('Account/fetchAccount');
  }
});

watch(network, async () => {
  // refresh data on-chain when network changes
  await refreshWeb3States();
  window.dispatchEvent(new Event('m-refresh-widgets'));
});

watch(authState, async () => {
  if (isLoggedIn.value) {
    setTimeout(() => {
      if (window.innerWidth <= 640) {
        window.scrollTo({
          top: (window.innerHeight * 0.5),
          behavior: 'smooth'
        });
      }
    }, 200);
  }
  window.dispatchEvent(new Event('m-refresh-widgets'));
});

// make web3 calls when provider is available
watch(isProviderAvailable, async () => {
  await refreshWeb3States();
});

onMounted(async () => {
  // get on-chain data on load
  await refreshWeb3States();
  await refershTipRecords();
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ConfirmMintDialog, {
      "is-open": _unref(isLoading),
      "mint-count": parseInt(numberToMint.value),
      "is-confirming": txConfirming.value,
      "claim-config": __props.claimConfig,
      claim: claim.value
    }, null, 8, ["is-open", "mint-count", "is-confirming", "claim-config", "claim"]),
    _createVNode(Frame, {
      id: "campaign-complete-frame",
      class: "m-project"
    }, {
      default: _withCtx(() => [
        (isLoggedIn.value)
          ? (_openBlock(), _createBlock(WalletConnection, {
              key: 0,
              id: "campaign-complete-wallet",
              "fallback-provider": __props.claimConfig?.fallbackProvider ?? null
            }, null, 8, ["fallback-provider"]))
          : _createCommentVNode("", true),
        (__props.claimConfig)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", {
                class: "campaign-complete-top-section",
                style: _normalizeStyle({ '--background-image': `url(${__props.claimConfig.image})` })
              }, [
                _hoisted_2,
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_4, [
                        (status.value === 'enabled' && !!__props.claimConfig?.endDate && !isSoldOut.value)
                          ? (_openBlock(), _createBlock(Countdown, {
                              key: 0,
                              id: "campaign-complete-info-countdown",
                              "end-date": __props.claimConfig?.endDate
                            }, null, 8, ["end-date"]))
                          : _createCommentVNode("", true),
                        (claim.value)
                          ? (_openBlock(), _createBlock(ClaimSummary, {
                              key: 1,
                              id: "campaign-complete-info-summary",
                              claim: claim.value,
                              claimConfig: __props.claimConfig
                            }, null, 8, ["claim", "claimConfig"]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(Action, {
                    id: "campaign-complete-info-action",
                    error: error.value,
                    status: status.value,
                    maxTokenPerWallet: maxTokenPerWallet.value,
                    authState: _unref(authState),
                    isSoldOut: isSoldOut.value ?? null,
                    claimConfig: __props.claimConfig,
                    allowedMintQuantity: allowedMintQuantity.value,
                    tokenMinted: tokenMinted.value ?? null,
                    networkError: networkError.value,
                    claimLimitType: claimLimitType.value,
                    numberToMint: numberToMint.value,
                    tipAmount: tipAmount.value,
                    allowMint: allowMint.value,
                    claimTokenStatus: _unref(claimTokenStatus),
                    claim: claim.value,
                    txLink: txLink.value,
                    tokenId: tokenId.value,
                    claimToken: _unref(claimToken),
                    onNumberToMintChanged: _cache[0] || (_cache[0] = ($event: any) => (numberToMint.value = $event)),
                    onTipAmountChanged: _cache[1] || (_cache[1] = ($event: any) => (tipAmount.value = $event))
                  }, null, 8, ["error", "status", "maxTokenPerWallet", "authState", "isSoldOut", "claimConfig", "allowedMintQuantity", "tokenMinted", "networkError", "claimLimitType", "numberToMint", "tipAmount", "allowMint", "claimTokenStatus", "claim", "txLink", "tokenId", "claimToken"])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(ImageLoader, {
                    id: "campaign-complete-media",
                    image: __props.claimConfig?.image,
                    media: __props.claimConfig?.animation ?? null,
                    "arweave-url": arweaveUrl.value ?? null
                  }, {
                    error: _withCtx(() => [
                      _createTextVNode(" Failed to load the image ")
                    ]),
                    _: 1
                  }, 8, ["image", "media", "arweave-url"])
                ])
              ], 4),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _hoisted_8,
                  _createVNode(ClaimDescription, {
                    id: "campaign-complete-info-description",
                    description: __props.claimConfig?.description
                  }, null, 8, ["description"]),
                  _createVNode(ViewDetails, {
                    id: "campaign-complete-info-details",
                    records: ([
                        arweaveUrl.value ? {
                          label: 'Arweave URL',
                          link: arweaveUrl.value,
                          value: _unref(abbreviate)(arweaveUrl.value.split('/').pop()!)
                        } : undefined,
                        {
                          label: 'Contract Address',
                          link: `https://etherscan.io/address/${__props.claimConfig?.creatorContractAddress}`,
                          value: _unref(abbreviateAddress)(__props.claimConfig?.creatorContractAddress)
                        },
                        ((claim.value as Claim1155Payable)?.tokenId) ? {
                          label: 'Token ID',
                          link: `https://etherscan.io/token/${__props.claimConfig?.creatorContractAddress}?a=${(claim.value as Claim1155Payable)?.tokenId}`,
                          value: (claim.value as Claim1155Payable)?.tokenId
                        } : undefined,
                        __props.claimConfig?.claimType ? {
                          label: 'Token Standard',
                          value: __props.claimConfig?.claimType.toUpperCase()
                        } : undefined,
                        {
                          label: 'Blockchain',
                          value: 'Ethereum'
                        },
                        __props.claimConfig?.startDate ? {
                          label: 'Start Date',
                          value: __props.claimConfig?.startDate.toISOString().slice(0, 10)
                        } : undefined,
                        __props.claimConfig?.endDate ? {
                          label: 'End Date',
                          value: __props.claimConfig?.endDate.toISOString().slice(0, 10)
                        } : undefined,
                        mediaArweaveUrl.value ? {
                          label: 'Original Media',
                          link: mediaArweaveUrl.value,
                          value: _unref(abbreviate)(mediaArweaveUrl.value.split('/').pop()!)
                        } : undefined,
                      ].filter(x => !!x) as Record[])
                  }, null, 8, ["records"]),
                  (membershipRecords.value.length > 0)
                    ? (_openBlock(), _createBlock(Holders, {
                        key: 0,
                        id: "campaign-complete-info-leaderboard",
                        membershipRecords: membershipRecords.value
                      }, null, 8, ["membershipRecords"]))
                    : _createCommentVNode("", true),
                  (__props.claimConfig && claim.value)
                    ? (_openBlock(), _createBlock(PlatformLinks, {
                        key: 1,
                        id: "campaign-complete-info-platform-links",
                        claimConfig: __props.claimConfig,
                        claim: claim.value
                      }, null, 8, ["claimConfig", "claim"]))
                    : _createCommentVNode("", true)
                ]),
                _hoisted_9,
                _createVNode(Footer)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})